import React from 'react'
import GenericTable from '../general/generic-table'
import { utilService } from '../../services/util-service'

interface GlueTriggerTableProps {
    data: any[]
    onEditClick?: (trigger: any) => void
}

const createMapping = (title: string, location: string[], type: string) => ({
    title, location, type
})

const mapping = [
    createMapping('Schedule Name', ['trigger_name'], 'string'),
    createMapping('Status', ['_status_display'], 'string'),
    createMapping('Schedule Type', ['schedule_type'], 'string'),
    createMapping('Schedule', ['_schedule_display'], 'string'),
    createMapping('Entity Names', ['_entity_names_display'], 'string'),
    createMapping('System Names', ['_system_names_display'], 'string'),
    createMapping('Description', ['description'], 'string'),
]

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function cronToHumanReadable(cronExpr: string): string {
    // AWS Glue cron: minute hour day-of-month month day-of-week year
    const parts = cronExpr.trim().split(/\s+/)
    if (parts.length < 6) return cronExpr

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts
    const timeParts: string[] = []

    // Convert UTC hour to human-readable time
    if (hour !== '*' && minute !== '*') {
        const h = parseInt(hour, 10)
        const m = parseInt(minute, 10)
        if (!isNaN(h) && !isNaN(m)) {
            const utcTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} UTC`
            timeParts.push(`at ${utcTime}`)
        }
    } else if (hour !== '*') {
        timeParts.push(`at ${hour}:00 UTC`)
    }

    // Day of week
    if (dayOfWeek !== '?' && dayOfWeek !== '*') {
        const dayNames = dayOfWeek.split(',').map(d => {
            const num = parseInt(d, 10)
            if (!isNaN(num) && num >= 1 && num <= 7) return DAYS_OF_WEEK[num - 1]
            // Handle SUN, MON, etc.
            const upper = d.toUpperCase()
            const match = DAYS_OF_WEEK.find(dw => dw.toUpperCase() === upper.substring(0, 3))
            return match || d
        })
        timeParts.push(`on ${dayNames.join(', ')}`)
    }

    // Day of month
    if (dayOfMonth !== '?' && dayOfMonth !== '*') {
        timeParts.push(`on day ${dayOfMonth} of the month`)
    }

    // Month
    if (month !== '*' && month !== '?') {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const monthDisplay = month.split(',').map(m => {
            const num = parseInt(m, 10)
            return (!isNaN(num) && num >= 1 && num <= 12) ? monthNames[num - 1] : m
        })
        timeParts.push(`in ${monthDisplay.join(', ')}`)
    }

    if (timeParts.length === 0) return `Every minute (${cronExpr})`

    // Check if it's daily (no specific day constraints)
    const isDaily = (dayOfWeek === '?' || dayOfWeek === '*') && (dayOfMonth === '?' || dayOfMonth === '*')
    const prefix = isDaily ? 'Daily' : 'Every'

    return timeParts.length === 1 && isDaily
        ? `${prefix} ${timeParts[0]}`
        : `${prefix} ${timeParts.join(', ')}`
}

function rateToHumanReadable(rateExpr: string): string {
    // e.g. "1 hour", "5 minutes", "2 days"
    const match = rateExpr.match(/^(\d+)\s+(minute|minutes|hour|hours|day|days)$/i)
    if (!match) return `Every ${rateExpr}`
    const value = parseInt(match[1], 10)
    const unit = match[2].toLowerCase().replace(/s$/, '')
    if (value === 1) return `Every ${unit}`
    return `Every ${value} ${unit}s`
}

function getScheduleDisplay(trigger: any): string {
    if (trigger.schedule_details?.expression) {
        const expr = trigger.schedule_details.expression
        if (trigger.schedule_type === 'cron') return cronToHumanReadable(expr)
        if (trigger.schedule_type === 'rate') return rateToHumanReadable(expr)
        return expr
    }
    if (trigger.conditional_details) {
        const conditions = trigger.conditional_details.conditions || []
        return conditions.map((c: any) => `After ${c.JobName || c.CrawlerName || ''} ${(c.State || '').toLowerCase()}`).join(', ')
    }
    return 'On Demand'
}

function getSystemDisplayName(jobAction: any): string {
    const jobName = jobAction.job_name || ''
    if (jobName === 'generic2s3') {
        return jobAction.arguments?.['--system_name'] || 'generic'
    }
    return jobName.replace(/2s3$/, '')
}

function enrichTrigger(trigger: any): any {
    const jobActions = trigger.job_actions || []
    const systemNames = Array.from(new Set(jobActions.map(getSystemDisplayName))) as string[]

    const stateMap: Record<string, string> = {
        'ACTIVATED': 'Active',
        'DEACTIVATED': 'Inactive',
        'CREATED': 'Created',
        'DELETING': 'Updating',
        'UPDATING': 'Updating',
    }

    return {
        ...trigger,
        _status_display: stateMap[trigger.state] || trigger.state,
        _schedule_display: getScheduleDisplay(trigger),
        _entity_names_display: (trigger.entity_names || []).join(', '),
        _system_names_display: systemNames.join(', '),
    }
}

function getBaseName(triggerName: string): string {
    return triggerName.replace(/-\d+$/, '')
}

export function groupTriggers(triggers: any[]): any[] {
    const groups: Record<string, any[]> = {}
    for (const trigger of triggers) {
        const base = getBaseName(trigger.trigger_name)
        if (!groups[base]) groups[base] = []
        groups[base].push(trigger)
    }

    return Object.entries(groups).map(([baseName, group]) => {
        const first = group[0]
        const allJobActions = group.flatMap((t: any) => t.job_actions || [])
        const allEntityNames = Array.from(new Set(group.flatMap((t: any) => t.entity_names || []))) as string[]
        const allSystemNames = Array.from(new Set(group.flatMap((t: any) => t.system_names || []))) as string[]
        const anyInactive = group.some((t: any) => t.state !== 'ACTIVATED')

        return {
            ...first,
            trigger_name: baseName,
            job_actions: allJobActions,
            entity_names: allEntityNames,
            system_names: allSystemNames,
            state: anyInactive ? group.find((t: any) => t.state !== 'ACTIVATED')!.state : 'ACTIVATED',
            _grouped_triggers: group,
        }
    })
}

export const GlueTriggerTable: React.FC<GlueTriggerTableProps> = ({ data, onEditClick }) => {
    const grouped = groupTriggers(data)
    const enrichedData = grouped.map(enrichTrigger)

    const tableData: any[][] = enrichedData.map((record: any) =>
        mapping.map((m) => {
            const cell: any = {
                title: m.title,
                value: utilService.getCellValue(record, m),
                type: m.type,
            }
            if (m.title === 'Status' && record.state !== 'ACTIVATED') {
                cell.partialTooltip = 'This schedule is not activated and will not run'
            }
            return cell
        })
    )

    const handleEditClick = onEditClick ? (rowData: any[]) => {
        const nameCell = rowData.find((cell: any) => cell.title === 'Schedule Name')
        const baseName = nameCell?.value
        const rawItem = grouped.find((item: any) => item.trigger_name === baseName)
        if (rawItem) onEditClick(rawItem)
    } : undefined

    return (
        <GenericTable
            data={tableData}
            recordName="ETL Schedules"
            onEditClick={handleEditClick}
            editColumn="Schedule Name"
            filterableColumns={['Status', 'Schedule Type', 'Entity Names', 'System Names']}
            defaultSort={{ key: 'Schedule Name', direction: 'asc' }}
        />
    )
}
